import { useRef, useState, useCallback, useEffect } from "react";
import { usePing } from "../../utils/useMultipartUpload";
import { useLivenessDetectionSessionUIContext } from "./SessionUIController";

/** Constants for Recording */
const MAX_CHUNKS = 5;
const MIN_CHUNKS = 1;
const CAPTURE_INTERVAL = 1000;

export type timestampedBlob = {
  timestamp: number,
  data: Blob
}


const stopRecorder = (recording: MediaRecorder) => {
  if (recording.state === 'recording') {
    recording.stop();
  }
}

// Custom hook to manage interval recording
const hevcCodecs = [
  'video/mp4; codecs="avc1.42E01E"', // Baseline profile for H.264
  'video/mp4; codecs="avc1.64001F"', // Main profile for H.264
  'video/mp4; codecs="avc1.640028"', // High profile for H.264
  'video/mp4; codecs="hev1.1.6.L93.B0"', // HEVC (H.265)
  'video/mp4; codecs="hvc1.1.6.L93.B0"', // HEVC (H.265)
];

const webmCodecs = [
  'video/webm; codecs="vp8"',
  'video/webm; codecs="vp9"',
  'video/webm; codecs="av1"',
  'video/ogg; codecs="theora"'
];

export const useIntervalRecording = (stream: MediaStream | null) => {

  const { ping } = useLivenessDetectionSessionUIContext();
  type RecorderKey = number;
  const recordedChunks = useRef({} as Record<RecorderKey, timestampedBlob[]>);
  const mediaRecorders = useRef({} as Record<RecorderKey, MediaRecorder>);
  const [captureReady, setCaptureReady] = useState(false);
  const i = useRef(0);

  const codecFailures = useRef([] as string[]);
  const chosenCodec = useRef('');

  // Android devices prefer Webm, iPhones prefer HEVC, but try all just in case.
  // Some older macs don't support HEVC, so we try all.
  const codecs = [...hevcCodecs, ...webmCodecs];

  const startNewRecording = useCallback((stream: MediaStream | null) => {
    if (!stream) {
      return; 
    }
    
    const mediaRecorder = (() => {
      let recorder: MediaRecorder | undefined;
      for (let codec of codecs) {
        if (!MediaRecorder.isTypeSupported(codec)) continue;
        if (codecFailures.current.includes(codec)) continue;
        try {
          recorder = new MediaRecorder(stream, {
            mimeType: codec, 
            videoBitsPerSecond: 1500000,
          });
          chosenCodec.current = codec;
          break;
        } catch (e) {
          // Turns out, this doesn't really catch anything.
          console.log("Codec doesnt work with device: ", e, codec, navigator?.userAgent);
        }
      }
      if (!recorder) throw new Error("Device not supported");
      return recorder;
    })();

    const recorderKey = ++i.current;
    recordedChunks.current[recorderKey] = [];

    // the MDN docs are fuzzy on if this should be an Event or ErrorEvent, 
    // apparently Chrome thinks its an Event whereas safari/firefox think its an ErrorEvent... 
    // Meanwhile, the actual error info is stored in event.error
    mediaRecorder.onerror = (event: Event) => {
      if ((event as any).error.name === 'EncodingError') {
        // pick a different codec
        codecFailures.current.push(chosenCodec.current);
      } else {
        ping('Unknown Media recorder error', { error: { name: (event as any).error?.name, message: (event as any).error?.message } })
      }
    }

    mediaRecorder.ondataavailable = (event) => {
      let { data } = event;
      if (data.size > 0) {
        recordedChunks.current[recorderKey].push({ 
          timestamp: Date.now(),
          data,
        });

        if (recordedChunks.current[recorderKey].length >= MAX_CHUNKS) {
          // Stop the recording
          stopRecorder(mediaRecorder);
        }
        
      } else {
        console.warn("No data available", event);
        return;
      }

      if (recordedChunks.current[recorderKey]?.length >= MIN_CHUNKS) {
        setCaptureReady((cur) => {
          return true
        });
      } else {
        setCaptureReady(false);
      }

    };

    try {
      if (stream) mediaRecorder.start(CAPTURE_INTERVAL);
    } catch (e) {
      //ping('Error starting recorder', { e: (e as any).message, estr: (e as Error).toString() })
      console.warn("Error starting recorder:", e);
    }

    setTimeout(() => {
      stopRecorder(mediaRecorder);
    }, MAX_CHUNKS * CAPTURE_INTERVAL);

    mediaRecorders.current[recorderKey] = mediaRecorder;
  }, []);

  const recordingInterval = useRef(null as NodeJS.Timeout | null);

  useEffect(() => {
    startNewRecording(stream);
    recordingInterval.current = setInterval(() => startNewRecording(stream), CAPTURE_INTERVAL * MAX_CHUNKS);
    return () => {
      if (recordingInterval.current) clearInterval(recordingInterval.current);
      Object.values(mediaRecorders.current).forEach((mediaRecorder) => {
        stopRecorder(mediaRecorder);
      });
      recordedChunks.current = {};
      mediaRecorders.current = {};
    };
  }, [stream]);

  const stopRecording = useCallback(() => {
    Object.values(mediaRecorders.current).forEach((mediaRecorder) => {
      stopRecorder(mediaRecorder);
    });
    if (recordingInterval.current) clearInterval(recordingInterval.current);
  }, []);

  return { recordedChunks, codec: chosenCodec, captureReady, stopRecording };
};